import React from 'react';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { hasPermission, ROLES, getRoleName } from '../utils/rolePermissions';
import Shell from '../features/dashboard/Shell';
import { Link } from 'react-router-dom';
import './ProtectedPage.css';

/**
 * ProtectedPage Component
 * Wraps pages with role-based access control and shows access denied message
 * if user doesn't have the required permission.
 *
 * @param {ReactNode} children - The page content to render if access is granted
 * @param {string} permission - Permission string in format "feature:action" (e.g., "products:view")
 * @param {string} title - Page title shown in access denied message
 */
export default function ProtectedPage({ children, permission, title = 'this page' }) {
  const { user } = useSecureAuth();

  // Get user role, default to OWNER if not set (store owners have full access)
  const userRole = user?.merchantRole?.toUpperCase() || ROLES.OWNER;

  // Parse permission string
  const [feature, rawAction] = permission?.split(':') || [];

  // Map action names
  const actionMap = {
    'read': 'view',
    'write': 'edit',
    'view': 'view',
    'edit': 'edit',
    'create': 'create',
    'delete': 'delete'
  };

  const action = actionMap[rawAction] || rawAction;

  // Check if user has access
  const hasAccess = hasPermission(userRole, feature, action);

  if (hasAccess) {
    // Wrap in Shell so FeatureGate and other components render inside the layout
    return (
      <Shell>
        {children}
      </Shell>
    );
  }

  // Access denied - show friendly message within Shell layout
  return (
    <Shell>
      <div className="access-denied-container">
        <div className="access-denied-card">
          <div className="access-denied-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
          </div>

          <h1 className="access-denied-title">Access Denied</h1>

          <p className="access-denied-message">
            You don't have permission to access {title}.
          </p>

          <div className="access-denied-details">
            <p>
              Your role: <strong>{getRoleName(userRole)}</strong>
            </p>
            <p>
              This page requires <strong>{feature}</strong> access.
            </p>
          </div>

          <div className="access-denied-help">
            <p>
              If you believe you should have access to this page, please contact your store owner
              or administrator to adjust your permissions.
            </p>
          </div>

          <div className="access-denied-actions">
            <Link to="/dashboard" className="access-denied-btn primary">
              Go to Dashboard
            </Link>
            <button
              onClick={() => window.history.back()}
              className="access-denied-btn secondary"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

/**
 * Hook to check page-level permission
 * @param {string} feature - Feature name
 * @param {string} action - Action (view, edit, create, delete)
 * @returns {{ hasAccess: boolean, userRole: string }}
 */
export function usePagePermission(feature, action = 'view') {
  const { user } = useSecureAuth();
  const userRole = user?.merchantRole?.toUpperCase() || ROLES.OWNER;
  const hasAccess = hasPermission(userRole, feature, action);

  return { hasAccess, userRole };
}
